/**
 * Cliente do serviço Fábrica de Vídeos (services/fabrica-videos, 127.0.0.1:8105).
 * Same-origin: sessão S2 via nginx auth_request; escritas levam X-Token injetado só no servidor.
 * O browser nunca vê token nem a chave do Higgsfield.
 *
 * Rotas que GASTAM (gerar-cena, gerar-modulos) só são chamadas depois de a tela mostrar a
 * estimativa e a Mariana confirmar; o corpo leva {confirmo: 'GERAR'} e o serviço recusa sem isso.
 */
import type { Briefing, ModuloId } from './logic'

export class FabricaVideosApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'FabricaVideosApiError'
    this.status = status
  }
}

const BASE = '/api/fabrica-videos'

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(BASE + path, { credentials: 'include', ...init })
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as { erro?: string; detail?: unknown; message?: string } | null
    const detail = typeof corpo?.detail === 'string' ? corpo.detail : undefined
    throw new FabricaVideosApiError(resp.status, corpo?.erro || detail || corpo?.message || `Serviço da Fábrica de Vídeos respondeu HTTP ${resp.status}.`)
  }
  return resp.json() as Promise<T>
}

const json = (body: unknown, method = 'POST'): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

// ---- tipos do serviço (espelho de servico.py)

export type Dolar = { valor: number; data: string | null; origem: string; iof_pct: number }

export type Custos = { credits: number; usd: number; brl: number; geracoes: number; falhas: number; dias: number }

export type StatusServico = {
  ok: boolean
  higgsfield: { chave: boolean; base: string; modelos: Record<'cena' | 'video', { slug: string; campo_imagem: string; params: Record<string, unknown> }> }
  ffmpeg: { ffmpeg: boolean; ffprobe: boolean }
  texto: { chave: boolean; modelo: string }
  ml: { mongo: boolean }
  escrita_liberada: boolean
  dolar: Dolar
  custos_30d: Custos
  projetos: number
}

export type Preco = { credits: number; usd: number; brl?: number; slug?: string }

export type Estimativa = {
  em: string
  dolar: Dolar
  itens: { cena: Preco; modulo: Preco }
  total: { credits: number; usd: number; brl: number; brl_sem_iof: number }
  refazer_modulo: Preco & { brl: number }
  refazer_cena: Preco & { brl: number }
}

export type StatusPeca = 'nenhuma' | 'nenhum' | 'fila' | 'gerando' | 'ok' | 'erro'

export type Cena = Briefing['cena'] & {
  status: StatusPeca
  aprovacao: 'pendente' | 'aprovado'
  versoes: number
  status_api?: string
  url?: string
  url_publica?: string
  prompt?: string
  erro?: string
  em?: string
  estimativa?: Preco
  nota?: string
}

export type Modulo = {
  status: StatusPeca
  aprovacao: 'pendente' | 'aprovado'
  versoes: number
  status_api?: string
  url?: string
  prompt?: string
  erro?: string
  em?: string
  estimativa?: Preco
  sonda?: { duracao: number; largura: number; altura: number; audio: boolean } | null
}

export type Montagem = { status: 'nenhuma' | 'montando' | 'ok' | 'erro'; passo?: string; erro?: string; duracao?: number; tamanho?: number; arquivo?: string; em?: string }

export type PropostaFala = { texto: string; fonte: string; pendencia: string; palavras: number; acima_do_limite: boolean }
export type Proposta = { falas: Record<ModuloId, PropostaFala>; chamada: string; observacoes: string[]; modelo: string; em?: string }

export type Projeto = {
  id: string
  criado_em: string
  atualizado_em: string
  produto: Briefing['produto']
  chamada: string
  roteiro: Briefing['roteiro']
  insumos: Briefing['insumos']
  roteiro_aprovado: boolean
  proposta: Proposta | null
  estimativa: Estimativa | null
  cena: Cena
  modulos: Record<ModuloId, Modulo>
  montagem: Montagem
  custo_real: { credits: number; usd: number; brl: number; geracoes: number }
  anuncio?: { mlb: string; titulo: string; foto: string; permalink: string }
  erro: string | null
}

export type ResumoProjeto = {
  id: string
  mlb: string
  titulo: string
  conta: string
  criado_em: string
  atualizado_em: string
  etapa: 'roteiro' | 'cena' | 'modulos' | 'montagem' | 'pronto'
  cena: string
  modulos_aprovados: number
  montagem: string
  custo_usd: number
  custo_brl: number
}

// ---- chamadas

export const fetchStatus = () => api<StatusServico>('/status')
export const fetchCustos = (dias = 30) => api<{ ok: boolean } & Custos>(`/custos?dias=${dias}`)
export const fetchProjetos = () => api<{ ok: boolean; projetos: ResumoProjeto[] }>('/projetos')
export const fetchProjeto = (id: string) =>
  api<{ ok: boolean; projeto: Projeto; em_andamento: string[]; pendencias_roteiro: string[] }>(`/projetos/${id}`)

const briefingParaCorpo = (b: Briefing) => ({ produto: b.produto, chamada: b.chamada, roteiro: b.roteiro, cena: b.cena, insumos: b.insumos })

export const criarProjeto = (b: Briefing) => api<{ ok: boolean; projeto: Projeto }>('/projetos', json(briefingParaCorpo(b)))
export const salvarBriefing = (id: string, b: Briefing) => api<{ ok: boolean; projeto: Projeto }>(`/projetos/${id}`, json(briefingParaCorpo(b), 'PUT'))
export const editarProjeto = (id: string, payload: Record<string, unknown>) => api<{ ok: boolean; projeto: Projeto }>(`/projetos/${id}`, json(payload, 'PUT'))
export const apagarProjeto = (id: string) => api<{ ok: boolean }>(`/projetos/${id}`, { method: 'DELETE' })

export const proporRoteiro = (id: string) => api<{ ok: boolean; proposta: Proposta }>(`/projetos/${id}/propor-roteiro`, { method: 'POST' })
/** Grátis: créditos e preço antes de seguir. */
export const estimar = (id: string) => api<{ ok: boolean; estimativa: Estimativa }>(`/projetos/${id}/estimar`, { method: 'POST' })
/** GASTA. Só depois de mostrar a estimativa e a Mariana confirmar. */
export const gerarCena = (id: string, ajuste?: string) =>
  api<{ ok: boolean; mensagem: string }>(`/projetos/${id}/gerar-cena`, json({ confirmo: 'GERAR', ...(ajuste != null ? { ajuste } : {}) }))
/** GASTA (por módulo). Só depois de mostrar a estimativa e a Mariana confirmar. */
export const gerarModulos = (id: string, modulos?: ModuloId[]) =>
  api<{ ok: boolean; mensagem: string }>(`/projetos/${id}/gerar-modulos`, json({ confirmo: 'GERAR', ...(modulos ? { modulos } : {}) }))
/** 0 crédito: ffmpeg na VPS. */
export const montar = (id: string) => api<{ ok: boolean; mensagem: string }>(`/projetos/${id}/montar`, { method: 'POST' })

export const urlArquivo = (id: string, nome: 'cena.jpg' | 'final.mp4' | `${ModuloId}.mp4`, v?: string | number) =>
  `${BASE}/projetos/${id}/arquivo/${nome}${v != null ? `?v=${encodeURIComponent(String(v))}` : ''}`

export const fetchAnuncio = (mlb: string) =>
  api<{ ok: boolean; mlb: string; titulo: string; foto: string; fotos: string[]; permalink: string }>(`/anuncio/${encodeURIComponent(mlb)}`)
